/**
 * Performance monitoring utilities for tracking and optimizing performance
 */

interface PerformanceMetrics {
  operation: string;
  startTime: number;
  endTime?: number;
  duration?: number;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetrics> = new Map();

  /**
   * Start timing an operation
   * @param operation - The name of the operation to time
   * @returns A unique ID for this timing operation
   */
  start(operation: string): string {
    const id = `${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.metrics.set(id, {
      operation,
      startTime: performance.now(),
    });
    return id;
  }

  /**
   * End timing an operation
   * @param id - The ID returned from start()
   * @returns The duration in milliseconds
   */
  end(id: string): number {
    const metric = this.metrics.get(id);
    if (!metric) {
      console.warn(`Performance metric not found for ID: ${id}`);
      return 0;
    }

    const endTime = performance.now();
    const duration = endTime - metric.startTime;
    
    metric.endTime = endTime;
    metric.duration = duration;

    // Log performance metrics in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Performance: ${metric.operation} took ${duration.toFixed(2)}ms`);
    }

    return duration;
  }

  /**
   * Get all performance metrics
   */
  getMetrics(): PerformanceMetrics[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear();
  }

  /**
   * Get average duration for an operation type
   */
  getAverageDuration(operation: string): number {
    const operationMetrics = Array.from(this.metrics.values())
      .filter(m => m.operation === operation && m.duration !== undefined)
      .map(m => m.duration!);

    if (operationMetrics.length === 0) return 0;
    
    return operationMetrics.reduce((sum, duration) => sum + duration, 0) / operationMetrics.length;
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Hook for tracking performance of operations
 * @param operation - The name of the operation to track
 * @returns An object with start and end functions
 */
export function usePerformanceTracking(operation: string) {
  const start = () => performanceMonitor.start(operation);
  const end = (id: string) => performanceMonitor.end(id);
  
  return { start, end };
}

/**
 * Higher-order function to wrap async operations with performance tracking
 * @param operation - The name of the operation
 * @param fn - The async function to wrap
 * @returns The wrapped function with performance tracking
 */
export function withPerformanceTracking<T extends (...args: any[]) => Promise<any>>(
  operation: string,
  fn: T
): T {
  return (async (...args: Parameters<T>) => {
    const id = performanceMonitor.start(operation);
    try {
      const result = await fn(...args);
      return result;
    } finally {
      performanceMonitor.end(id);
    }
  }) as T;
}

/**
 * Debounce utility for optimizing frequent operations
 * @param func - The function to debounce
 * @param wait - The delay in milliseconds
 * @param immediate - Whether to call the function immediately on the first invocation
 * @returns The debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate = false
): T {
  let timeout: NodeJS.Timeout | null = null;
  
  return ((...args: Parameters<T>) => {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    
    const callNow = immediate && !timeout;
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    
    if (callNow) func(...args);
  }) as T;
}

/**
 * Throttle utility for limiting the rate of function execution
 * @param func - The function to throttle
 * @param limit - The time limit in milliseconds
 * @returns The throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): T {
  let inThrottle: boolean;
  
  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }) as T;
}

const defaultWebVitalsReporter = (metric: { name: string; value: number; id: string; delta?: number }) => {
  if (typeof window !== 'undefined') {
    const w = window as Window & { __KOLADA_WEB_VITALS__?: Record<string, unknown> };
    w.__KOLADA_WEB_VITALS__ = w.__KOLADA_WEB_VITALS__ || {};
    w.__KOLADA_WEB_VITALS__[metric.name] = metric;
  }
  if (process.env.NODE_ENV === 'development') {
    console.info(`[Web Vital] ${metric.name}:`, Math.round(metric.value), metric);
  }
};

/**
 * Observe Web Vitals (FCP, LCP, CLS, etc.). When no callback is passed, results are stored on
 * `window.__KOLADA_WEB_VITALS__` and logged in development — use with Performance panel / `duckdb-ready` mark.
 */
export function observeWebVitals(onPerfEntry?: (metric: any) => void) {
  const reporter =
    onPerfEntry && typeof onPerfEntry === 'function' ? onPerfEntry : defaultWebVitalsReporter;
  import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    getCLS(reporter);
    getFID(reporter);
    getFCP(reporter);
    getLCP(reporter);
    getTTFB(reporter);
  });
}

/**
 * Log performance metrics to console
 */
export function logPerformanceMetrics() {
  const metrics = performanceMonitor.getMetrics();
  
  if (metrics.length > 0) {
    console.group('Performance Metrics');
    metrics.forEach(metric => {
      if (metric.duration !== undefined) {
        console.log(`${metric.operation}: ${metric.duration.toFixed(2)}ms`);
      }
    });
    console.groupEnd();
  }
}